<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import CalendarView from './CalendarView.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { accountScopedUrl } = useAccount();

const activeView = ref('list');
const statusFilter = ref('');

const records = computed(
  () => getters['scheduledMessages/getAccountScheduledMessages'].value
);
const uiFlags = computed(() => getters['scheduledMessages/getUIFlags'].value);

const statusOptions = ['pending', 'sent', 'failed', 'cancelled'];

const statusBadgeClass = status => {
  return (
    {
      pending: 'bg-n-amber-3 text-n-amber-11',
      sent: 'bg-n-teal-3 text-n-teal-11',
      failed: 'bg-n-ruby-3 text-n-ruby-11',
      cancelled: 'bg-n-slate-3 text-n-slate-11',
    }[status] || 'bg-n-slate-3 text-n-slate-11'
  );
};

const tableHeaders = computed(() => [
  t('SCHEDULED_MESSAGES.LIST.TABLE_HEADER.STATUS'),
  t('SCHEDULED_MESSAGES.LIST.TABLE_HEADER.CONTENT'),
  t('SCHEDULED_MESSAGES.LIST.TABLE_HEADER.CONTACT'),
  t('SCHEDULED_MESSAGES.LIST.TABLE_HEADER.CHANNEL'),
  t('SCHEDULED_MESSAGES.LIST.TABLE_HEADER.SCHEDULED_AT'),
  t('SCHEDULED_MESSAGES.LIST.TABLE_HEADER.ACTION'),
]);

const fetchScheduledMessages = () => {
  store.dispatch('scheduledMessages/getAll', {
    status: statusFilter.value || undefined,
  });
};

const cancelScheduledMessage = async id => {
  try {
    await store.dispatch('scheduledMessages/cancelAccountWide', id);
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_ERROR'));
  }
};

onMounted(fetchScheduledMessages);
watch(statusFilter, fetchScheduledMessages);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingAll"
    :loading-message="t('SCHEDULED_MESSAGES.LOADING')"
    :no-records-found="activeView === 'list' && !records.length"
    :no-records-message="t('SCHEDULED_MESSAGES.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('SCHEDULED_MESSAGES.HEADER')"
        :description="t('SCHEDULED_MESSAGES.DESCRIPTION')"
      >
        <template #actions>
          <div
            class="flex items-center gap-2 bg-n-slate-2 rounded-lg p-1 border border-n-weak"
          >
            <button
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="
                activeView === 'list'
                  ? 'bg-n-solid-1 text-n-iris-9 shadow-sm'
                  : 'text-n-slate-11'
              "
              @click="activeView = 'list'"
            >
              {{ t('SCHEDULED_MESSAGES.VIEW.LIST') }}
            </button>
            <button
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="
                activeView === 'calendar'
                  ? 'bg-n-solid-1 text-n-iris-9 shadow-sm'
                  : 'text-n-slate-11'
              "
              @click="activeView = 'calendar'"
            >
              {{ t('SCHEDULED_MESSAGES.VIEW.CALENDAR') }}
            </button>
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div v-if="activeView === 'list'">
        <div class="flex items-center gap-2 mb-4">
          <Button
            v-for="option in statusOptions"
            :key="option"
            :label="t(`SCHEDULED_MESSAGES.LIST.STATUS.${option.toUpperCase()}`)"
            sm
            :faded="statusFilter !== option"
            :slate="statusFilter !== option"
            @click="statusFilter = statusFilter === option ? '' : option"
          />
        </div>
        <BaseTable
          :headers="tableHeaders"
          :items="records"
          :no-data-message="t('SCHEDULED_MESSAGES.LIST.404')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="scheduledMessage in items"
              :key="scheduledMessage.id"
              :item="scheduledMessage"
            >
              <template #default>
                <BaseTableCell>
                  <span
                    class="px-2 py-0.5 rounded-full text-xs font-semibold"
                    :class="statusBadgeClass(scheduledMessage.status)"
                  >
                    {{
                      t(
                        `SCHEDULED_MESSAGES.LIST.STATUS.${scheduledMessage.status.toUpperCase()}`
                      )
                    }}
                  </span>
                  <span
                    v-if="scheduledMessage.error_message"
                    v-tooltip.top="scheduledMessage.error_message"
                    class="ml-1 i-lucide-info size-3.5 text-n-ruby-9 inline-block align-middle"
                  />
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12 line-clamp-1">
                    {{ scheduledMessage.content }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <router-link
                    :to="
                      accountScopedUrl(
                        `conversations/${scheduledMessage.conversation_id}`
                      )
                    "
                    class="text-n-iris-9 hover:underline"
                  >
                    {{ scheduledMessage.contact?.name || '—' }}
                  </router-link>
                </BaseTableCell>
                <BaseTableCell>
                  <div
                    v-if="scheduledMessage.inbox"
                    class="flex items-center gap-1.5"
                  >
                    <i
                      :class="
                        getInboxIconByType(
                          scheduledMessage.inbox.channel_type,
                          null,
                          'line'
                        )
                      "
                      class="size-3.5 text-n-slate-11 shrink-0"
                    />
                    <span class="text-body-main text-n-slate-11 truncate">
                      {{ scheduledMessage.inbox.name }}
                    </span>
                  </div>
                  <span v-else>—</span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{
                      new Date(scheduledMessage.scheduled_at).toLocaleString()
                    }}
                  </span>
                </BaseTableCell>
                <BaseTableCell align="end">
                  <Button
                    v-if="scheduledMessage.status === 'pending'"
                    v-tooltip.top="t('SCHEDULED_MESSAGES.CANCEL')"
                    icon="i-lucide-x"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="cancelScheduledMessage(scheduledMessage.id)"
                  />
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>
      <CalendarView
        v-else
        :messages="records"
        @cancel="cancelScheduledMessage"
      />
    </template>
  </SettingsLayout>
</template>
